import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowSquareOut, ArrowUp, ArrowDown, Plus, Trash } from "@phosphor-icons/react";

const slugify = (l) => (l.toLowerCase().startsWith("other") ? "other" : l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60));

const Card = ({ title, hint, children, testid }) => (
  <div className="border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="font-display text-base font-black tracking-tight">{title}</div>
    {hint && <p className="mt-1 text-xs text-[#6B7280]">{hint}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

export default function AdminHomepage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site/homepage");
      setCfg(data);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const setService = (i, k, v) => setCfg((c) => ({ ...c, services: c.services.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const moveService = (i, d) => setCfg((c) => {
    const arr = [...c.services]; const j = i + d;
    if (j < 0 || j >= arr.length) return c;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...c, services: arr };
  });
  const addService = () => setCfg((c) => ({ ...c, services: [...c.services.slice(0, -1), { slug: "", label: "", strip: true }, c.services[c.services.length - 1]] }));
  const removeService = (i) => setCfg((c) => ({ ...c, services: c.services.filter((_, j) => j !== i) }));
  const setCard = (i, k, v) => setCfg((c) => ({ ...c, core_services: c.core_services.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        services: cfg.services.map((s) => ({ label: s.label, strip: !!s.strip })),
        core_services: cfg.core_services.map((c) => ({ slug: c.slug, title: c.title, desc: c.desc, price: c.price || "", href: c.href })),
      };
      const { data } = await api.put("/site/admin/homepage", payload);
      setCfg(data);
      toast.success("Homepage saved — live now, no deploy needed");
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="p-6 text-sm text-[#6B7280]" data-testid="hp-admin-loading">Loading…</div>;
  const serviceSlugs = cfg.services.map((s) => ({ slug: s.slug || slugify(s.label), label: s.label }));

  return (
    <div className="space-y-6 p-6" data-testid="admin-homepage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight">Homepage</h1>
          <p className="mt-1 text-sm text-[#6B7280]">Six core service cards + the one services list that drives both the capability strip and the quote-form dropdown.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold text-[#0044FF]" data-testid="hp-admin-view-page">View homepage <ArrowSquareOut size={14} /></a>
      </div>

      <Card title="Six core service cards" hint="Leave Price blank to publish the card without a price. Link is where the whole card goes (e.g. /junk-removal) — change it any time without a deploy." testid="hp-cards-card">
        <div className="space-y-3">
          {cfg.core_services.map((c, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 border border-[#E5E7EB] p-3 md:grid-cols-[1.1fr_2fr_0.8fr_1fr_1.1fr]" data-testid={`hp-card-${i}`}>
              <div><label className="text-[11px] font-bold text-[#6B7280]">Title</label><Input value={c.title} onChange={(e) => setCard(i, "title", e.target.value)} data-testid={`hp-card-${i}-title`} /></div>
              <div><label className="text-[11px] font-bold text-[#6B7280]">Description</label><Input value={c.desc} onChange={(e) => setCard(i, "desc", e.target.value)} data-testid={`hp-card-${i}-desc`} /></div>
              <div><label className="text-[11px] font-bold text-[#6B7280]">Price (optional)</label><Input value={c.price || ""} onChange={(e) => setCard(i, "price", e.target.value)} placeholder="From $125" data-testid={`hp-card-${i}-price`} /></div>
              <div><label className="text-[11px] font-bold text-[#6B7280]">Link</label><Input value={c.href} onChange={(e) => setCard(i, "href", e.target.value)} placeholder="/junk-removal" data-testid={`hp-card-${i}-href`} /></div>
              <div>
                <label className="text-[11px] font-bold text-[#6B7280]">Quote-form service</label>
                <select value={c.slug} onChange={(e) => setCard(i, "slug", e.target.value)} className="h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid={`hp-card-${i}-slug`}>
                  {serviceSlugs.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Services list (${cfg.services.length})`} hint="Order here = order in the quote-form dropdown. 'In strip' shows the service as a chip in 'And Everything Else a Property Needs'. Only list trades the network can actually staff." testid="hp-services-card">
        <div className="space-y-2">
          {cfg.services.map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border border-[#F3F4F6] p-2" data-testid={`hp-service-${i}`}>
              <Input value={s.label} onChange={(e) => setService(i, "label", e.target.value)} className="min-w-[200px] flex-1" placeholder="Service name" data-testid={`hp-service-${i}-label`} />
              <label className="inline-flex items-center gap-1.5 text-xs font-bold text-[#374151]">
                <input type="checkbox" checked={!!s.strip} onChange={(e) => setService(i, "strip", e.target.checked)} data-testid={`hp-service-${i}-strip`} /> In strip
              </label>
              <span className="w-40 truncate font-mono text-[10px] text-[#9CA3AF]">{s.slug || slugify(s.label)}</span>
              <Button variant="ghost" size="sm" onClick={() => moveService(i, -1)} aria-label="Move up"><ArrowUp size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => moveService(i, 1)} aria-label="Move down"><ArrowDown size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => removeService(i)} aria-label="Remove" data-testid={`hp-service-${i}-remove`}><Trash size={14} /></Button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="outline" className="mt-3" onClick={addService} data-testid="hp-add-service"><Plus size={14} /> Add service</Button>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="hp-save">{saving ? "Saving…" : "Save homepage"}</Button>
        {cfg.updated_at && <span className="text-xs text-[#6B7280]">Last saved {new Date(cfg.updated_at).toLocaleString()}</span>}
      </div>
    </div>
  );
}
